import cn from 'classnames';
import dynamic from 'next/dynamic';
import { noop } from 'lodash';

const ReactECharts = dynamic(() => import('echarts-for-react'), {
  ssr: false,
});
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import { addCohostModalInstance } from 'components/AddCohostModal';
import { Toggle } from 'components/form';

import { useCohost } from 'hooks/useCohost';
import { useCommon } from 'hooks/useCommon';
import { useEvent } from 'hooks/useEvent';
import { useNotification } from 'hooks/useNotification';

import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { TouchpointState } from 'lib/models/opportunity';
import { useSocietiesSearch } from 'lib/contexts/societies-search-context';
import { formatToK } from 'utils/format';
import EventCloneModal from '../EventCloneModal';
import Guests from '../Guests';
import InDraftCard from '../InDraftCard';
import InReviewCard from '../InReviewCard';
import NewCountBadge from '../NewCountBadge';
import PendingInviteCard from '../PendingInviteCard';
import PendingLinkNotification from '../PendingLinkNotification';
import PendingLocationNotification from '../PendingLocationNotification';
import WaitingCard from '../WaitingCard';
import { useAttendeesFilter } from '../useAttendeesFilter';
import { inviteGuestModalInstance } from '../InviteGuestModal';
import { graphOption, weeklyComparison } from './utils';

import styles from './Overview.module.scss';

export const Overview = (): ReactElement => {
  const router = useRouter();
  const { id } = router.query;
  const { isManager } = useAuth();
  const { onShare } = useCommon();

  const [t] = useTranslation('event-dashboard');

  const { touchpoint, mutateTouchpoint, touchpointDashboard } =
    useAttendeesFilter();
  const { setInvitePopup } = useCampaigns();

  const [notifyMe, setNotifyMe] = useState<boolean>(true);
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const { addCohost } = useCohost();
  const notificationInstance = useNotification();
  const { setSearch } = useSocietiesSearch();

  const {
    title,
    creatable_for,
    cohost_application,
    is_online,
    online_link,
    place,
    short_slug,
    notify_creatable,
    state,
  } = touchpoint || {};
  const isCohost = cohost_application ? true : false;

  const { toggleNotifyMe } = useEvent();

  useEffect(() => {
    setNotifyMe(!!notify_creatable);
  }, [notify_creatable]);

  const graphs = useMemo(() => {
    if (touchpointDashboard) {
      const registrationsDataPoints = Object.values(
        touchpointDashboard?.registrations_per_day_30
      );
      if (registrationsDataPoints.length === 1) {
        registrationsDataPoints.unshift(0);
      }
      const registrationsGraph = (
        <ReactECharts
          option={graphOption(
            registrationsDataPoints,
            new Array(registrationsDataPoints.length).fill('')
          )}
          opts={{ renderer: 'svg' }}
          style={{ height: '64px', width: '128px' }}
        />
      );
      const viewsDataPoints = Object.values(
        touchpointDashboard?.views_per_day_30
      );
      if (viewsDataPoints.length === 1) {
        viewsDataPoints.unshift(0);
      }
      const viewsGraph = (
        <ReactECharts
          option={graphOption(
            viewsDataPoints,
            new Array(viewsDataPoints.length).fill('')
          )}
          opts={{ renderer: 'svg' }}
          style={{ height: '64px', width: '128px' }}
        />
      );
      return { registrationsGraph, viewsGraph };
    }
    return null;
  }, [touchpointDashboard]);

  const getTrends = useMemo(() => {
    if (touchpointDashboard) {
      const { percentageChange: percentRegistrations } = weeklyComparison(
        touchpointDashboard?.registrations_per_day_30
      );
      const { percentageChange: percentViews } = weeklyComparison(
        touchpointDashboard?.views_per_day_30
      );

      const registrations = {
        up: !(percentRegistrations < 0),
        percentage: Math.abs(percentRegistrations),
      };

      const views = {
        up: !(percentViews < 0),
        percentage: Math.abs(percentViews),
      };
      return { registrations, views };
    }
    return null;
  }, [touchpointDashboard]);

  if (!touchpoint) {
    return <></>;
  }

  const { id: hostId } = creatable_for
    ? parseResponse<{
        id: string;
      }>(creatable_for)
    : { id: '' };
  const isHost = !cohost_application ? true : false;

  const onAddCohostClick = async () => {
    if (hostId) {
      try {
        const cohostIds = await addCohostModalInstance({
          idsForCohost: {
            creatableForId: hostId,
            touchpointId: id as string,
          },
        });
        if (cohostIds.length > 0) {
          for (let i = 0; i < cohostIds.length; i++) {
            const response = await addCohost(id as string, cohostIds[i]);
            if (!response) {
              break;
            } else {
              mutateTouchpoint();
              notificationInstance.success({
                title:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-title')
                    : t('add-co-host-message.invitations-title'),
                message:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-message')
                    : t('add-co-host-message.invitations-message'),
              });
            }
          }
        }
        setSearch('');
      } catch (error) {
        noop;
      }
    }
  };

  return (
    <section className={styles.overview}>
      {state === 'ready_for_approval' && <InReviewCard />}
      {state === 'draft' && isHost && <InDraftCard />}
      {state && state !== 'expired' && (
        <>
          <PendingInviteCard />
          {isHost && <WaitingCard />}
          {isHost && is_online && !online_link ? (
            <PendingLinkNotification
              touchpoint={touchpoint}
              className={styles.notification}
            />
          ) : null}
          {isHost && !is_online && !place.data ? (
            <PendingLocationNotification
              touchpoint={touchpoint}
              className={styles.notification}
            />
          ) : null}
        </>
      )}
      <div className={styles.statsWrapper}>
        <div className={styles.stats}>
          <div className={cn(styles.block, styles.registrations)}>
            <div className={styles.blockRow}>
              <span className={styles.blockTitle}>
                {t('overview.head.title')}
              </span>
              {isHost ? (
                <div className={styles.notifyToggle}>
                  {t('overview.head.notify-me')}
                  <Toggle
                    className={styles.toggle}
                    checked={notifyMe}
                    onChange={async () => {
                      await toggleNotifyMe({
                        id: id as string,
                        notify_creatable: !notifyMe,
                      });
                      setNotifyMe(!notifyMe);
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
            <div className={styles.blockRow}>
              <div>
                <div className={styles.count}>
                  {touchpointDashboard &&
                    (isHost
                      ? touchpointDashboard.status_counter?.applied || 0
                      : (touchpointDashboard.status_counter?.applied || 0) -
                        touchpointDashboard.waitlist_count)}{' '}
                  {touchpointDashboard?.unseen_waitlist_count ? (
                    <NewCountBadge
                      count={t('overview.head.new-count', {
                        count: touchpointDashboard.unseen_waitlist_count,
                      })}
                    />
                  ) : (
                    <></>
                  )}
                </div>
                {getTrends ? (
                  <div className={styles.weekTrend}>
                    <span
                      className={cn({
                        [styles.up]: getTrends.registrations.up,
                        [styles.down]: !getTrends.registrations.up,
                      })}
                    >
                      <Icon
                        iconName={
                          getTrends.registrations.up
                            ? 'icon_arrow-up-2'
                            : 'icon_arrow-down-2'
                        }
                      />{' '}
                      {getTrends.registrations.percentage}%
                    </span>{' '}
                    {t('overview.head.this-week')}
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
              <div>{graphs?.registrationsGraph}</div>
            </div>
          </div>
          <div className={styles.line}></div>
          <div className={cn(styles.block, styles.views)}>
            <div className={styles.blockRow}>
              <span className={styles.blockTitle}>
                {t('overview.head.views')}
              </span>
            </div>
            <div className={styles.blockRow}>
              <div>
                <div className={styles.count}>
                  {formatToK(touchpointDashboard?.viewed || 0)}
                </div>
                {getTrends ? (
                  <div className={styles.weekTrend}>
                    <span
                      className={cn({
                        [styles.up]: getTrends.views.up,
                        [styles.down]: !getTrends.views.up,
                      })}
                    >
                      <Icon
                        iconName={
                          getTrends.views.up
                            ? 'icon_arrow-up-2'
                            : 'icon_arrow-down-2'
                        }
                      />{' '}
                      {getTrends.views.percentage}%
                    </span>{' '}
                    {t('overview.head.this-week')}
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
              <div>{graphs?.viewsGraph}</div>
            </div>
          </div>
        </div>
        {!['ready_for_approval', 'draft'].includes(state) && (
          <div className={styles.buttonWrapper}>
            {state === 'expired' ? (
              <div
                className={styles.button}
                onClick={() => {
                  router.push(
                    PAGE_ROUTES.EVENT_MANAGEMENT_DASHBOARD.replace(
                      '[id]',
                      id as string
                    ) + '?tab=feedback'
                  );
                }}
              >
                <div className={styles.buttonTitle}>
                  <Icon
                    iconName={'icon_star-outline'}
                    className={styles.icon}
                  />
                  {t('overview.head.buttons.view-feedback')}
                </div>
                <div className={styles.buttonIcon}>
                  <Icon iconName="icon_arrow-right-2" />
                </div>
              </div>
            ) : isManager && state === TouchpointState.PUBLISHED ? (
              <div
                className={styles.button}
                onClick={async () => {
                  setInvitePopup(true);
                  await inviteGuestModalInstance({ touchpoint });
                  mutateTouchpoint();
                }}
              >
                <div className={styles.buttonTitle}>
                  <Icon iconName={'icon_mail'} className={styles.icon} />
                  Invite guests
                </div>
                <div className={styles.buttonIcon}>
                  <Icon iconName="icon_arrow-right-2" />
                </div>
              </div>
            ) : null}
            <div
              className={styles.button}
              onClick={() => {
                onShare(title, `${window.location.origin}/${short_slug}`);
              }}
            >
              <div className={styles.buttonTitle}>
                <Icon iconName={'icon_link'} className={styles.icon} />
                {t('common.share')}
              </div>
              <div className={styles.buttonIcon}>
                <Icon iconName="icon_arrow-right-2" />
              </div>
            </div>
            {state === 'expired' ? (
              <div
                className={styles.button}
                onClick={() => setShowCloneModal(true)}
              >
                <div className={styles.buttonTitle}>
                  <Icon iconName={'icon_copy'} className={styles.icon} />
                  {t('overview.head.buttons.clone-event')}
                </div>
                <div className={styles.buttonIcon}>
                  <Icon iconName="icon_arrow-right-2" />
                </div>
              </div>
            ) : (
              !isCohost && (
                <div className={styles.button} onClick={onAddCohostClick}>
                  <div className={styles.buttonTitle}>
                    <Icon iconName={'icon_badge'} className={styles.icon} />
                    {t('overview.head.buttons.invite-co-host')}
                  </div>
                  <div className={styles.buttonIcon}>
                    <Icon iconName="icon_arrow-right-2" />
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
      <Guests isOverview />
      <EventCloneModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
      />
    </section>
  );
};
